import { createApi } from "@reduxjs/toolkit/query/react";
import { retryRateLimited, retryUnlessClientError } from "../retryConditions";
import { createRetryingBaseQuery } from "../retryingBaseQuery";
import { formatUuidT } from "../../flatbuffers/display";
import type { ExternalEndpointT } from "../../flatbuffers/generated/api/external-endpoint";
import type {
  CreateExternalArgs,
  ExternalDetail,
  ExternalDetailsResponse,
  ExternalEndpointCatalog,
  ExternalMutationResponse,
  ExternalMutationResult,
  RotateExternalSecretArgs,
  RotatedExternalSecret,
  UpdateExternalGrantsArgs,
  UpdateExternalNameArgs,
  UpdateExternalRoleArgs,
} from "../../interfaces/External.interface";

const maxRetries = 2;

// POST and PUT /externals/ answer a FlatBuffer caller with Api.External, which
// has no field for role_id or the one-time client secret. Asking for JSON is
// the only way to receive the secret, so these two say so explicitly and the
// base query leaves their Accept alone.
const JSON_ONLY = "application/json";

// A 4xx (a refused role, an out-of-scope client) is the same on every attempt,
// so it is shown at once rather than after the backoff.
const extraOptions = { retryCondition: retryUnlessClientError(maxRetries) };

// POST /externals/ is not idempotent: a 500 after the External was saved (a
// grant it could not attach, say) would create one more on every retry. Only
// a 429, which the server refused before doing anything, is sent again.
const createOptions = { retryCondition: retryRateLimited(() => false, maxRetries) };

// LiftCloud caps a page at 500; the endpoint catalog is a few dozen routes.
const ENDPOINT_CATALOG_LIMIT = 500;

const baseQuery = createRetryingBaseQuery(
  process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE ?? "",
  maxRetries,
);

/**
 * API clients (LiftCloud Externals), LIFT-1098.
 *
 * Every endpoint here answers JSON, which the FlatBuffer base query's JSON
 * fallback parses: the list is GET /externals/details (the FlatBuffer list at
 * GET /externals/ carries neither organization nor role), and POST/PUT send
 * Accept: application/json so the one-time client secret survives. Bodies go out as JSON too, so
 * role_id keeps its three meanings (id, "" to clear, absent to keep). The
 * endpoint catalog is the one FlatBuffer reply: GET /external-endpoints/
 * answers Api.PaginatedExternalEndpoints whatever the Accept header says.
 */
export const externalsAPI = createApi({
  reducerPath: "externalsAPI",
  baseQuery,
  keepUnusedDataFor: 0,
  tagTypes: ["tagGetExternals", "tagGetExternalEndpoints"],
  refetchOnMountOrArgChange: true,
  refetchOnReconnect: true,
  endpoints: (builder) => ({
    //* *********************************************************** */
    //* GET API CLIENTS OF ONE ORGANIZATION */
    getExternalDetails: builder.query({
      query: ({ token, orgId }: { token: string; orgId: string }) => ({
        url: `/externals/details?org_id=${encodeURIComponent(orgId)}`,
        method: "GET",
        headers: { authorization: token },
      }),
      transformResponse: (response: ExternalDetailsResponse): ExternalDetail[] =>
        response?.data ?? [],
      extraOptions,
      providesTags: ["tagGetExternals"],
    }),
    //* *********************************************************** */
    //* CREATE API CLIENT */
    postExternal: builder.mutation({
      query: ({ token, name, orgId, roleId, permissions }: CreateExternalArgs) => ({
        url: "/externals/",
        method: "POST",
        body: {
          name,
          org_id: orgId,
          ...(roleId ? { role_id: roleId } : {}),
          ...(permissions && permissions.length > 0 ? { permissions } : {}),
        },
        headers: { authorization: token, Accept: JSON_ONLY },
      }),
      transformResponse: (response: ExternalMutationResponse): ExternalMutationResult =>
        response.data,
      extraOptions: createOptions,
      invalidatesTags: ["tagGetExternals"],
    }),
    //* *********************************************************** */
    //* CHANGE AN API CLIENT'S ROLE */
    putExternalRole: builder.mutation({
      query: ({ token, id, name, roleId }: UpdateExternalRoleArgs) => ({
        url: `/externals/${id}`,
        method: "PUT",
        body: { name, role_id: roleId },
        headers: { authorization: token, Accept: JSON_ONLY },
      }),
      transformResponse: (response: ExternalMutationResponse): ExternalMutationResult =>
        response.data,
      extraOptions,
      invalidatesTags: ["tagGetExternals"],
    }),
    //* *********************************************************** */
    //* RENAME AN API CLIENT */
    putExternalName: builder.mutation({
      query: ({ token, id, name }: UpdateExternalNameArgs) => ({
        url: `/externals/${id}`,
        method: "PUT",
        body: { name },
        headers: { authorization: token, Accept: JSON_ONLY },
      }),
      transformResponse: (response: ExternalMutationResponse): ExternalMutationResult =>
        response.data,
      extraOptions,
      invalidatesTags: ["tagGetExternals"],
    }),
    //* *********************************************************** */
    //* ROTATE AN API CLIENT'S SECRET */
    // Not idempotent: every accepted request issues another secret, so only a
    // 429 is sent again (createOptions).
    postRotateExternalSecret: builder.mutation({
      query: ({ token, id, graceSeconds, revokeTokens }: RotateExternalSecretArgs) => ({
        url: `/externals/${id}/secret`,
        method: "POST",
        body: { grace_seconds: graceSeconds, revoke_tokens: revokeTokens },
        headers: { authorization: token, Accept: JSON_ONLY },
      }),
      transformResponse: (response: RotatedExternalSecret): RotatedExternalSecret => response,
      extraOptions: createOptions,
    }),
    //* *********************************************************** */
    //* CHANGE THE ENDPOINTS AN API CLIENT MAY CALL */
    putExternalGrants: builder.mutation({
      query: ({ token, id, name, permissions, permissions2delete }: UpdateExternalGrantsArgs) => ({
        url: `/externals/${id}`,
        method: "PUT",
        body: { name, permissions, permissions2delete },
        headers: { authorization: token, Accept: JSON_ONLY },
      }),
      transformResponse: (response: ExternalMutationResponse): ExternalMutationResult =>
        response.data,
      extraOptions,
      invalidatesTags: ["tagGetExternals"],
    }),
    //* *********************************************************** */
    //* THE ENDPOINTS AN API CLIENT CAN BE GRANTED */
    getExternalEndpoints: builder.query({
      query: ({ token }: { token: string }) => ({
        url: `/external-endpoints/?limit=${ENDPOINT_CATALOG_LIMIT}`,
        method: "GET",
        headers: { authorization: token },
      }),
      transformResponse: (response: {
        data?: ExternalEndpointT[];
        total?: number;
      }): ExternalEndpointCatalog => {
        const rows = response?.data ?? [];
        const endpoints = rows
          .map((endpoint) => ({
            id: formatUuidT(endpoint.id),
            uri: String(endpoint.uri ?? ""),
            verbs: endpoint.verbs.map(String),
            active: endpoint.active,
          }))
          .filter((endpoint) => endpoint.id !== "" && endpoint.uri !== "");
        // A row dropped here (no id or uri) is one the page cannot see, so
        // the catalog is as incomplete as one cut short by the page limit.
        return {
          endpoints,
          complete: (response?.total ?? rows.length) <= endpoints.length,
        };
      },
      extraOptions,
      providesTags: ["tagGetExternalEndpoints"],
    }),
    //* *********************************************************** */
    //* DELETE API CLIENT */
    deleteExternal: builder.mutation({
      query: ({ token, id }: { token: string; id: string }) => ({
        url: `/externals/${id}`,
        method: "DELETE",
        headers: { authorization: token },
      }),
      extraOptions,
      invalidatesTags: ["tagGetExternals"],
    }),
  }),
});

export const {
  useGetExternalDetailsQuery,
  useLazyGetExternalDetailsQuery,
  usePostExternalMutation,
  usePutExternalRoleMutation,
  usePutExternalGrantsMutation,
  usePutExternalNameMutation,
  usePostRotateExternalSecretMutation,
  useLazyGetExternalEndpointsQuery,
  useDeleteExternalMutation,
} = externalsAPI;
